import React, { useCallback, useEffect, useState } from "react";
import BinahMonitor from "./BinahMonitor";
import { useCameras, useDisableZoom } from "../hooks";
import UAParser from "ua-parser-js";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import OtpInput from "Pages/positiveScore/PositiveScore";
import OnBoarding from "Pages/OnBoarding";
import CloseScreen from "./CloseScreen";
import AddBMIForm from "./AddBMIForm";
import WelcomeScreen from "./WelcomeScreen";
import ConsentScreen from "../Pages/OnBoarding/Terms";
import PanCard from "./PanCard";
import axios from "axios";

// Higher-Order Component for Protected Route
function ProtectedRoute({ children }: { children: JSX.Element }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const password = localStorage.getItem("userPassword");

  useEffect(() => {
    const checkPassword = async () => {
      try {
        const response = await axios.post(
          "https://pivv-be.yhealth.me/api/validate-code",
          { code: password },
          {
            headers: {
              "x-api-key":
                "zrEjEPASSLwXksLSBrPtIHTBowGWhttrLvH00sZ96VRNPruYlePN8ZlujLCuyCoqQBbf52TLdCxHZCDAs05NbNZ4zGUDBNuOa7G88CYMvz21lHzy6FI7AXabiOPrtQOE",
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.success) {
          localStorage.setItem("userPassword", password);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("API Call Error:", error);
        setIsAuthenticated(false);
      }
    };

    checkPassword();
  }, [password]);

  if (isAuthenticated === null) {
    return <div>Loading...</div>; // Show loading state while checking
  }

  return isAuthenticated ? children : <Navigate to="/" replace />;
}

// NotFound Page Component
function NotFound() {
  const navigate = useNavigate();

  const handleBackToLogin = () => {
    navigate("/");
  };

  return (
    <div style={{ textAlign: "center", marginTop: "20vh" }}>
      <h1>404 - Page Not Found</h1>
      <p>The page you are looking for does not exist.</p>
      <button
        onClick={handleBackToLogin}
        style={{
          marginTop: "20px",
          padding: "10px 20px",
          fontSize: "16px",
          cursor: "pointer",
          backgroundColor: "#007bff",
          color: "#fff",
          border: "none",
          borderRadius: "5px",
        }}
      >
        Back to Login
      </button>
    </div>
  );
}

export default function App() {
  const cameras = useCameras();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [cameraId, setCameraId] = useState<string>();
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [isMobile] = useState(
    UAParser(navigator.userAgent).device.type === "mobile"
  );
  const [isCamerasLoading, setIsCamerasLoading] = useState(true);
  useDisableZoom();

  const updateLicenseStatus = useCallback((valid) => {
    setIsLicenseValid(valid);
  }, []);

  const toggleSettingsClick = useCallback(() => {
    setIsSettingsOpen(!isSettingsOpen);
  }, [isSettingsOpen]);

  const handleCloseSettings = useCallback(({ cameraId }) => {
    setCameraId(cameraId);
    setIsSettingsOpen(false);
  }, []);

  useEffect(() => {
    if (!cameras?.length) {
      setIsCamerasLoading(true);
      return;
    }
    setIsCamerasLoading(false);
    setCameraId(cameras[0].deviceId);
  }, [cameras]);

  // Add a retry mechanism for camera initialization
  const handleRetryCamera = useCallback(async () => {
    try {
      setIsCamerasLoading(true);
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === "videoinput");
      if (videoDevices.length > 0) {
        setCameraId(videoDevices[0].deviceId);
      }
    } catch (error) {
      console.error("Error retrying camera initialization:", error);
    } finally {
      setIsCamerasLoading(false);
    }
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Route */}
        <Route path="/" element={<WelcomeScreen />} />

        {/* Protected Routes */}
        <Route
          path="/onBoarding"
          element={
            <ProtectedRoute>
              <OnBoarding />
            </ProtectedRoute>
          }
        />
        <Route
          path="/terms"
          element={
            <ProtectedRoute>
              <ConsentScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/addBmi"
          element={
            <ProtectedRoute>
              <AddBMIForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scan"
          element={
            <ProtectedRoute>
              {isCamerasLoading ? (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100vh' 
                }}>
                  <h2>Initializing camera...</h2>
                  <button 
                    onClick={handleRetryCamera}
                    style={{
                      padding: '10px 20px',
                      marginTop: '20px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Retry Camera
                  </button>
                </div>
              ) : (
                <BinahMonitor
                  showMonitor={!(isMobile && isSettingsOpen)}
                  cameraId={cameraId}
                  onLicenseStatus={updateLicenseStatus}
                  isSettingsOpen={isSettingsOpen}
                />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/pan-card"
          element={
            <ProtectedRoute>
              <PanCard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/finalScore"
          element={
            <ProtectedRoute>
              <OtpInput />
            </ProtectedRoute>
          }
        />
        <Route
          path="/close"
          element={
            <ProtectedRoute>
              <CloseScreen />
            </ProtectedRoute>
          }
        />

        {/* Not Found Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}
